import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import "./ExperienceCard.scss";

export default function ExperienceCard({ cardInfo, isDark }) {
  const [expanded, setExpanded] = useState(false);
  const [dominantColor, setDominantColor] = useState(null);
  const imgRef = useRef(null);
  const cardRef = useRef(null);
  const modalContentRef = useRef(null);

  // Fallback color generator
  const generateFallbackColor = (companyName) => {
    let hash = 0;
    for (let i = 0; i < companyName?.length; i++) {
      hash = companyName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const r = 50 + Math.abs(hash % 150);
    const g = 50 + Math.abs((hash >> 8) % 150);
    const b = 50 + Math.abs((hash >> 16) % 150);

    return `rgb(${r}, ${g}, ${b})`;
  };

  useEffect(() => {
    // Function to extract average color from an image using canvas
    const extractColor = (img) => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Small canvas for performance
        canvas.width = 50;
        canvas.height = 50;

        // Draw and get color data
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

        // Calculate average RGB
        let r = 0,
          g = 0,
          b = 0,
          count = 0;

        // Sample pixels (skip every few for performance)
        for (let i = 0; i < imageData?.length; i += 20) {
          r += imageData[i];
          g += imageData[i + 1];
          b += imageData[i + 2];
          count++;
        }

        // Calculate average and ensure vibrant but not too bright colors
        r = Math.min(255, Math.max(50, Math.floor(r / count)));
        g = Math.min(255, Math.max(50, Math.floor(g / count)));
        b = Math.min(255, Math.max(50, Math.floor(b / count)));

        return `rgb(${r}, ${g}, ${b})`;
      } catch (error) {
        console.error("Color extraction failed:", error);
        return generateFallbackColor(cardInfo.company);
      }
    };

    // Set initial fallback color
    if (!dominantColor) {
      setDominantColor(generateFallbackColor(cardInfo.company));
    }

    const img = imgRef.current;
    if (img) {
      if (img.complete) {
        // Image is already loaded
        const color = extractColor(img);
        setDominantColor(color);
      } else {
        // Set up load event handler
        const handleLoad = () => {
          requestAnimationFrame(() => {
            const color = extractColor(img);
            setDominantColor(color);
          });
        };

        img.addEventListener("load", handleLoad);
        return () => img.removeEventListener("load", handleLoad);
      }
    }
  }, [cardInfo.company, dominantColor]);

  useEffect(() => {
    // Handle outside clicks to close expanded card
    const handleClickOutside = (event) => {
      if (expanded && cardRef.current && !cardRef.current.contains(event.target)) {
        setExpanded(false);
      }
    };

    // Add event listener when expanded
    if (expanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expanded]);

  // Lock/unlock body scroll and reset modal scroll position
  useEffect(() => {
    if (expanded) {
      document.body.style.overflow = "hidden";
      // Reset scroll position of modal content
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = 0;
      }
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [expanded]);

  const toggleExpand = () => {
    if(!expanded && window.tracker) {
      window.tracker.track(`view_experience_${cardInfo.company || "-"}`, {
        company: cardInfo.company,
        role: cardInfo.role
      });
    }
    setExpanded(!expanded);
    
  };

  // Truncate description by character count
  const getTruncatedDesc = (text, maxLength = 75) => {
    if (!text) return "";
    if (text?.length <= maxLength) return text;

    const lastSpace = text.substring(0, maxLength).lastIndexOf(" ");
    const truncateAt = lastSpace > 0 ? lastSpace : maxLength;

    return text.substring(0, truncateAt) + "...";
  };

  const GetDescBullets = ({ descBullets, isDark }) => {
    if (!descBullets) return null;

    return (
      <ul className="experience-bullets">
        {descBullets.map((item, i) => (
          <li
            key={i}
            className={isDark ? "subTitle dark-mode-text" : "subTitle"}
          >
            {item}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      {/* Regular card */}
      <div
        className={`${isDark ? "experience-card-dark" : "experience-card"}`}
        onClick={!expanded ? toggleExpand : undefined}
      >
        <div style={{ background: dominantColor }} className="experience-banner">
          <div className="experience-blurred_div"></div>
          <div className="experience-div-company">
            <h5 className="experience-text-company">{cardInfo.company}</h5>
          </div>

          <img
            ref={imgRef}
            crossOrigin="anonymous"
            className="experience-roundedimg"
            src={cardInfo.companylogo}
            alt={cardInfo.company}
          />
        </div>
        <div className="experience-text-details">
          <h5
            className={
              isDark
                ? "experience-text-role dark-mode-text"
                : "experience-text-role"
            }
          >
            {cardInfo.role}
          </h5>
          <h5
            className={
              isDark
                ? "experience-text-date dark-mode-text"
                : "experience-text-date"
            }
          >
            {cardInfo.date}
          </h5>

          <p
            className={
              isDark
                ? "subTitle experience-text-desc dark-mode-text"
                : "subTitle experience-text-desc"
            }
          >
            {getTruncatedDesc(cardInfo.desc)}
          </p>

          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand();
            }}
            className={`view-details-btn ${isDark ? "dark-mode-btn" : ""}`}
          >
            View Details
          </button>
        </div>
      </div>

      {/* Expanded modal view with portal */}
      {expanded &&
        createPortal(
          <div className="experience-modal-overlay">
            <div
              ref={cardRef}
              className={`experience-modal ${isDark ? "experience-modal-dark" : ""}`}
              style={{
                "--card-color": dominantColor,
              }}
            >
              <button
                className="experience-modal-close"
                onClick={toggleExpand}
                aria-label="Close"
              >
                ×
              </button>

              <div className="experience-modal-header">
                <img
                  className="experience-modal-img"
                  src={cardInfo.companylogo}
                  alt={cardInfo.company}
                />
                <div className="experience-modal-title">
                  <h3>{cardInfo.company}</h3>
                  <h4>{cardInfo.role}</h4>
                  <p className="experience-modal-date">{cardInfo.date}</p>
                </div>
              </div>

              <div ref={modalContentRef} className="experience-modal-content">
                <div className="experience-modal-description">
                  <h5>Description</h5>
                  <p>{cardInfo.desc}</p>
                </div>

                {cardInfo.descBullets && (
                  <div className="experience-modal-bullets">
                    <h5>Key Achievements</h5>
                    <GetDescBullets descBullets={cardInfo.descBullets} isDark={isDark} />
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}